import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Benutzer, FunktionJeUnternehmen, Unternehmen }
  from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, Objektliste, Objektzeile,
         useBeforeUnload, useFormularZustand, useBestaetigung } from "@kundeninfo/ui";
import { Auswahl, Chip, Formular } from "@kundeninfo/ui/me-design";
import type { VerlassenWaechter } from "../../router.js";
import { Meldung, formatKonfliktZeit, meldungAus } from "./gemeinsam.js";
import type { VerwaltungBereichTeile } from "./gemeinsam.js";
import { UnterschriftBlock } from "./Unterschrift.js";
import { KiKnopf } from "../../components/meHuellen.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich BENUTZER: Rolle und Heimatunternehmen je Person.
 *
 * Beides ist der Grund, aus dem es die Verwaltung überhaupt gibt (Betriebsbefund B2 — bis
 * dahin nur per SQL setzbar).
 *
 * **Gestalt seit dem Entscheid vom 06.09.2026.** Die Benutzerliste steht LINKS im Inhalt,
 * rechts daneben die gewählte Person. Vor Etappe 2 war der Bereich EINE Tabelle mit einem
 * Auswahlfeld und einem Speichern-Knopf je Zeile — bei zwanzig Kolleginnen zwanzig
 * gleichnamige Felder untereinander; zwischen Etappe 2 und diesem Entscheid stand die
 * Liste als zweite Ebene in der Verwaltungs-Spalte, wo sie neben dem Bereichs-Untermenü
 * redundant war.
 *
 * Deshalb ein HOOK und keine Komponente: siehe `VerwaltungBereichTeile` in gemeinsam.tsx.
 *
 * **Geändert wird im PANEL** (Hausregel „striktes Modalpanel", Entscheid Dirk vom
 * 06.09.2026): Das Detail rechts LIEST — Name, E-Mail, Rolle, Heimatunternehmen —, und
 * „Ändern" öffnet ein Modal mit den zwei Auswahlfeldern und der Fußleiste
 * Speichern/Abbrechen. Bis dahin standen die Felder offen im Inhalt: Man konnte sie
 * anfassen, weggehen und nie erfahren, dass etwas Ungespeichertes zurückblieb. Die
 * Fachregeln sind davon unberührt — derselbe Schreibweg, derselbe Versionsvergleich,
 * derselbe Letzter-Admin-Schutz, derselbe Verlassen-Wächter.
 *
 * **ANGELEGT wird hier nicht und GELÖSCHT auch nicht.** Benutzer entstehen bei der
 * Anmeldung über Keycloak; die Anwendung kennt keinen Schreibweg dafür (der Klient unten
 * hat genau zwei Methoden). Deshalb hat die Liste — anders als beim Unternehmen — auch
 * keinen Anlege-Knopf: Das ist die Fachlage, keine Lücke dieser Aufgabe.
 *
 * **FUNKTION JE UNTERNEHMEN** (Belegdesign Etappe 2, Entscheid 74): Im Panel steht unter
 * Rolle und Heimatunternehmen eine Zeile je Unternehmen; gedruckt wird die Funktion des
 * belegführenden Unternehmens unter der Unterschrift, wenn der Unterschriftsplatz
 * „Funktion darunter“ trägt. Gespeichert wird mit DEMSELBEN Knopf: erst Rolle und
 * Heimat (mit Versionsvergleich, 409 → fremder Stand), dann die geänderten Funktionen
 * (`PUT …/funktionen`, ohne Version; 422 hängt am Feld der Zeile). Das Detail liest sie.
 *
 * **CSS:** Alles hier trägt den Block `vw__` (views/Verwaltung.css) — Liste wie Formular
 * stehen im Inhalt; der Spaltenblock `vws__` gehört allein dem Bereichs-Untermenü.
 */

/** Nur die zwei Wege dieses Bereichs — so lässt er sich im Test mit zwei vi.fn zeichnen,
 *  ohne den ganzen ApiClient nachzubauen (Muster: BereichArtikelClient). */
// seit dem Layout-Editor auch die Unterschrift
export type BenutzerClient = Pick<ApiClient,
  "listUsers" | "setUserRole" | "setUserSignature" | "deleteUserSignature" |
  "userSignatureUrl" | "listUserJobTitles" | "setUserJobTitles">;

/** Der Entwurf einer Person. `funktionen` hält NUR die angefassten Zeilen (Kennung des
 *  Unternehmens → Text); eine fehlende Zeile gilt mit dem geladenen Stand. */
type BenutzerEntwurf = {
  role: Benutzer["role"]; homeCompanyId: string | null; funktionen: Record<string, string>;
};

/** Die geladenen Funktionen einer Person — oder warum sie (noch) nicht da sind. */
type FunktionsStand =
  | { art: "laedt" } | { art: "fehler"; text: string }
  | { art: "da"; items: FunktionJeUnternehmen[] };

/** Der Hinweis unter dem Abschnitt im Panel (Brief Aufgabe 5, Spec Entscheid 74). */
export const FUNKTION_HINWEIS = "Erscheint unter der Unterschrift, wenn der "
  + "Unterschriftsplatz „Funktion darunter“ trägt.";

/*
 * Die Regel der Funktion — bewusst hier NACHGEBILDET (`web` darf `@kundeninfo/domain` nicht
 * importieren, scripts/check-boundaries.mjs), mit DENSELBEN Grenzen und Worten wie
 * `funktionFehler` in packages/domain/src/layoutRules.ts. Der Server prüft verbindlich; hier
 * geht es darum, dass ein Befund das Speichern blockiert, BEVOR die Rolle geschrieben ist
 * (Review 5, W1).
 */
const FUNKTION_MAX = 80;
const FUNKTION_UNGUELTIG = "FUNKTION_UNGUELTIG";
// eslint-disable-next-line no-control-regex
const STEUERZEICHEN = /[\u0000-\u001f\u007f-\u009f\ufffe\uffff]/;
const EINZELNES_SURROGAT = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;

/** `null` = gültig, sonst die Meldung fürs Feld (Text bereits gekappt, nicht leer). */
export function funktionFehler(text: string): string | null {
  if (text.length > FUNKTION_MAX) return `Text bis ${FUNKTION_MAX} Zeichen erwartet.`;
  if (STEUERZEICHEN.test(text) || EINZELNES_SURROGAT.test(text)) return "Enthält Steuerzeichen.";
  if (/[<>{}"]/.test(text)) return 'Die Zeichen < > { } " sind nicht erlaubt.';
  return null;
}

/** Die Meldung, wenn die Rolle gespeichert ist, die Funktionen aber nicht (Review 5, W1):
 *  kein stiller Teilstand — das Panel bleibt mit dieser Zeile stehen. */
export const TEILSTAND_MELDUNG = "Rolle gespeichert, Funktionen nicht — bitte erneut speichern.";

function funktionsFeld(companyId: string): string {
  return `funktionen.${companyId}`;
}

const ROLLEN: Benutzer["role"][] = ["EDITOR", "LEAD", "ADMIN"];

const ROLLEN_LABEL: Record<Benutzer["role"], string> = {
  EDITOR: "Bearbeiter (EDITOR)",
  LEAD: "Leitung (LEAD)",
  ADMIN: "Administrator (ADMIN)",
};

/**
 * Ersatztext für den Fachfehler, der ohne Feldangabe ankommen könnte.
 *
 * Im Normalfall zeigt die Maske die Meldung des SERVERS — sie ist genauer (sie nennt den
 * nächsten Schritt), und zwei Fassungen desselben Satzes liefen über die Zeit auseinander.
 * Dieser Text greift nur, wenn die 422 kein Feld mitbringt: Dann stünde sonst gar nichts da.
 */
const LETZTER_ADMIN_ERSATZ =
  "Der letzte Administrator kann nicht herabgestuft werden.";

function alsBenutzer(wert: unknown): Benutzer | null {
  if (typeof wert !== "object" || wert === null) return null;
  const b = wert as Partial<Benutzer>;
  if (typeof b.id !== "string" || typeof b.version !== "number") return null;
  return wert as Benutzer;
}

/**
 * Die Eigenschaften des Bereichs — dieselbe Gestalt wie beim Unternehmen nebenan und wie
 * bei den Bereichen der Etappe 1: `aktiv` als Riegel gegen mitlaufende Abfragen an fremden
 * Adressen, die Kennung aus der ADRESSE, ein Rückruf für den Adresswechsel. Navigiert wird
 * nicht hier — die Adresse kennt App.tsx.
 */
export type VerwaltungBenutzerProps = {
  aktiv: boolean;
  client: BenutzerClient;
  /** Die sichtbaren Konzernunternehmen — Auswahlliste für das Heimatunternehmen. */
  companies: Unternehmen[];
  /** Der geöffnete Benutzer aus der Adresse (`/verwaltung/benutzer/:id`). */
  benutzerId: string | null;
  onOeffnen: (benutzerId: string | null) => void;
  /** Der Verlassen-Wächter des Routers (Hausmuster, siehe KundeStammdaten). Weglassbar —
   *  dann fragt niemand, was für einen Prüfharnisch ohne Router genau richtig ist. */
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

export function useVerwaltungBenutzer(
  p: VerwaltungBenutzerProps,
): VerwaltungBereichTeile {
  const { aktiv, benutzerId } = p;

  const [zeilen, setZeilen] = useState<Benutzer[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  /** Die noch nicht gespeicherten Änderungen je Person. Fehlt ein Eintrag, lebt sie aus
   *  dem geladenen Stand — nach dem Speichern wird der Entwurf deshalb GELÖSCHT. */
  const [entwuerfe, setEntwuerfe] = useState<Record<string, Partial<BenutzerEntwurf>>>({});
  /** Die geladenen Funktionen je Person (Belegdesign Etappe 2) — geladen, sobald die
   *  Person geöffnet wird; das Detail liest sie, das Panel ändert sie. */
  const [funktionen, setFunktionen] = useState<Record<string, FunktionsStand>>({});
  /** Der Stand, den der Server mit einer 409 schickt — „Neu laden" übernimmt ihn. */
  const fremderStand = useRef<Benutzer | null>(null);
  /**
   * WESSEN Änderungspanel offen steht — `null` heisst „keines" (Hausregel „striktes
   * Modalpanel", 06.09.2026: Bearbeiten läuft im Modal, nicht im Inhalt).
   *
   * Eine KENNUNG und kein blosses Ja/Nein, aus demselben Grund wie überall in dieser
   * Datei: Das Panel gehört der Person, über der es geöffnet wurde. Es ist bewusst KEIN
   * Adressbestandteil — die Adresse benennt das Objekt, nicht den Zustand der Oberfläche.
   */
  const [aendern, setAendern] = useState<string | null>(null);

  const listUsers = p.client.listUsers;
  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listUsers()
      .then((antwort) => {
        if (!abgebrochen) {
          setZeilen(antwort.items);
          // Ein geglücktes Nachladen HEILT den alten Fehler (Fixrunde 1) — siehe die
          // ausführliche Begründung in unternehmen.tsx nebenan: Der Hook lebt über der
          // ganzen Anwendung, sein Zustand überlebt jeden Bereichswechsel.
          setLadeFehler(null);
        }
      })
      .catch((fehler: unknown) => {
        if (!abgebrochen) {
          setLadeFehler(meldungAus(fehler, "Die Benutzer konnten nicht geladen werden."));
        }
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listUsers]);

  /*
   * Beim VERLASSEN des Bereichs fällt alles Angefangene weg (Etappe-1-Lektion aus
   * Aufgabe 3): Entwürfe, Fehler und Konfliktkästen sind Bearbeitungszustände ohne eigene
   * Adresse und gehören zu einer Absicht, die man verlassen hat.
   */
  /*
   * Die Funktionen der GEÖFFNETEN Person laden — bei jedem Öffnen neu (eine Kollegin kann
   * sie inzwischen geändert haben). Eine späte Antwort für eine inzwischen verlassene
   * Person landet unter IHRER Kennung und stört niemanden.
   *
   * Der Aufruf steht in einer Referenz statt in der Abhängigkeitsliste: Ein Aufrufer, der
   * den Klienten bei jedem Zeichnen neu baut, löste sonst über den sofort gesetzten
   * Ladezustand eine Endlosschleife aus. Laden soll allein das Öffnen einer Person.
   */
  const listUserJobTitles = useRef(p.client.listUserJobTitles);
  listUserJobTitles.current = p.client.listUserJobTitles;
  useEffect(() => {
    if (!aktiv || benutzerId === null) return;
    const id = benutzerId;
    setFunktionen((alt) => ({ ...alt, [id]: { art: "laedt" } }));
    listUserJobTitles.current(id)
      .then((antwort) => {
        setFunktionen((alt) => ({ ...alt, [id]: { art: "da", items: antwort.items } }));
      })
      .catch((fehler: unknown) => {
        setFunktionen((alt) => ({ ...alt, [id]: { art: "fehler",
          text: meldungAus(fehler, "Die Funktionen konnten nicht geladen werden.") } }));
      });
  }, [aktiv, benutzerId]);

  useEffect(() => {
    if (aktiv) return;
    setEntwuerfe({});
    formular.zuruecksetzen();
    setAendern(null);
    // Auch der LADEFEHLER (Fixrunde 1, wie nebenan): Beim nächsten Betreten läuft die
    // Abfrage neu — bis ihre Antwort da ist, soll die Auskunft „lädt" heissen.
    setLadeFehler(null);
  }, [aktiv]);

  /*
   * Das Änderungspanel fällt mit der KENNUNG — dieselbe Bindung wie bei der
   * Stilllegen-Rückfrage nebenan (Fixrunde 1). Der Render-Guard unten versteckt es bei
   * fremder Adresse nur; ohne dieses Nullen stünde es nach Zurück/Vorwärts unaufgefordert
   * wieder da, und sein Speichern-Knopf arbeitete an einer Person, die niemand ansieht.
   */
  useEffect(() => {
    setAendern(null);
    // Und die Befunde mit: Sie gehören der Person, an der gespeichert wurde.
    formular.zuruecksetzen();
  }, [benutzerId]);

  /** Der Name eines Heimatunternehmens fürs LESENDE Detail — „keines", wo keines
   *  hinterlegt ist, und die blosse Kennung, falls das Unternehmen nicht in der Auswahl
   *  steht (etwa weil es stillgelegt wurde): Eine leere Zeile verschwiege, dass da etwas
   *  hängt. */
  function heimatName(id: string | null): string {
    if (id === null) return "keines";
    const c = p.companies.find((x) => x.id === id);
    return c === undefined ? id : `${c.shortCode} · ${c.name}`;
  }

  function entwurfVon(z: Benutzer): BenutzerEntwurf {
    const d = entwuerfe[z.id] ?? {};
    return { role: d.role ?? z.role,
             homeCompanyId: "homeCompanyId" in d ? d.homeCompanyId ?? null : z.homeCompanyId,
             funktionen: d.funktionen ?? {} };
  }

  /** Die geladenen Funktionen einer Person — leer, solange sie nicht da sind. */
  function geladeneFunktionen(id: string): FunktionJeUnternehmen[] {
    const stand = funktionen[id];
    return stand?.art === "da" ? stand.items : [];
  }

  /** Die GEÄNDERTEN Funktionen einer Person als Schreibwunsch — verglichen mit dem
   *  geladenen Stand, nach dem Kappen der Ränder (Leerzeichen allein ändern nichts). */
  function funktionsAenderungen(z: Benutzer): { companyId: string; jobTitle: string | null }[] {
    const e = entwurfVon(z);
    return geladeneFunktionen(z.id).flatMap((f) => {
      const text = e.funktionen[f.companyId];
      if (text === undefined) return [];
      const neu = text.trim();
      return neu === (f.jobTitle ?? "") ? []
        : [{ companyId: f.companyId, jobTitle: neu === "" ? null : neu }];
    });
  }

  function rolleGeaendert(z: Benutzer): boolean {
    const e = entwurfVon(z);
    return e.role !== z.role || e.homeCompanyId !== z.homeCompanyId;
  }

  function istGeaendert(z: Benutzer): boolean {
    return rolleGeaendert(z) || funktionsAenderungen(z).length > 0;
  }

  const satz = benutzerId === null
    ? null
    : (zeilen ?? []).find((z) => z.id === benutzerId) ?? null;

  /**
   * OB gerade bearbeitet wird — als Ableitung aus der ADRESSE, nicht als eigenes Ja/Nein
   * (Etappe-1-Lektion aus Aufgabe 3): Ein freistehendes Bool überlebte dort den
   * Adresswechsel und sperrte die Spaltenliste für immer.
   */
  const bearbeitung = satz !== null && istGeaendert(satz);

  /*
   * Das ECHTE Verlassen der Seite (Tab zu, neu laden, andere Anwendung) — Bestandsverhalten
   * aus dem Paket 2a, unverändert: Es fragt bei JEDEM ungespeicherten Entwurf nach, auch
   * bei einem, der zu einer gerade nicht geöffneten Person gehört. Der Browser kennt keine
   * Adresse innerhalb der Anwendung; was er verhindern soll, ist der Verlust von Eingaben
   * überhaupt.
   */
  const etwasGeaendert = (zeilen ?? []).some(istGeaendert);
  useBeforeUnload(etwasGeaendert);

  const { bestaetigen, bestaetigung } = useBestaetigung();

  /**
   * Der Verlassen-Wächter des ROUTERS (Hausmuster, siehe KundeStammdaten): Solange die
   * geöffnete Person ungespeicherte Änderungen trägt, fragt jede Navigation nach — der
   * Rückweg im Kopf des Einspalters ebenso wie die Zurück-Taste. (Ein Klick auf eine
   * andere Person in der Liste kommt hier nicht an: Die Zeilen sind während der
   * Bearbeitung gesperrt.)
   *
   * Bestätigtes Verlassen VERWIRFT den Entwurf (Fixrunde 1) — die ausführliche Begründung
   * steht am gleichnamigen Wächter in unternehmen.tsx: Sonst sagte die Rückfrage die
   * Unwahrheit, denn die Entwürfe liegen je Kennung und stünden beim Zurückkommen wieder da.
   */
  const registriereVerlassenWaechter = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!bearbeitung || registriereVerlassenWaechter === undefined
      || benutzerId === null) return;
    return registriereVerlassenWaechter(async () => {
      const weiter = await bestaetigen({
        titel: "Seite verlassen?",
        text: "Dieser Benutzer hat ungespeicherte Änderungen. Sie gehen verloren.",
        bestaetigenText: "Verlassen",
        gefaehrlich: true,
      });
      if (weiter) vergiss(benutzerId);
      return weiter;
    });
    // `vergiss` steht bewusst nicht in der Liste — siehe dieselbe Notiz in unternehmen.tsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bearbeitung, registriereVerlassenWaechter, benutzerId]);

  function aendere(z: Benutzer, teil: Partial<BenutzerEntwurf>): void {
    setEntwuerfe((e) => ({ ...e, [z.id]: { ...e[z.id], ...teil } }));
  }

  function aendereFunktion(z: Benutzer, companyId: string, text: string): void {
    setEntwuerfe((e) => ({ ...e, [z.id]: { ...e[z.id],
      funktionen: { ...e[z.id]?.funktionen, [companyId]: text } } }));
  }

  /** Nur Rolle und Heimat vergessen — „Neu laden" nach einer 409 betrifft allein sie; die
   *  Funktionen tragen keine Version und bleiben, wie sie eingegeben sind. */
  function vergissRolle(id: string): void {
    setEntwuerfe((e) => {
      const alt = e[id];
      if (alt === undefined) return e;
      const rest = { ...e };
      if (alt.funktionen === undefined) delete rest[id];
      else rest[id] = { funktionen: alt.funktionen };
      return rest;
    });
  }

  function vergiss(id: string): void {
    setEntwuerfe((e) => {
      const rest = { ...e };
      delete rest[id];
      return rest;
    });
  }

  /**
   * Formularzustände des Änderungspanels (ME-Etappe 3, Aufgabe 6) — es steht immer nur
   * EINES offen, deshalb genügt ein Zustand für den ganzen Bereich. `laeuft` sperrt die
   * zwei Auswahlen und den Fuß; eine 422 hängt an der Auswahl (LETZTER_ADMIN ohne Feld an
   * der Rolle, mit dem eigenen Satz); eine 409 hält die Eingaben als „fremder Stand" fest —
   * „Neu laden" übernimmt den Stand aus der 409 (vorher der Kasten „Fremden Stand
   * übernehmen"). Die eigene Eingabe weicht ihm auf ausdrücklichen Klick, nie
   * stillschweigend.
   */
  const formular = useFormularZustand<Benutzer>({
    speichern: async () => {
      const z = satz;
      if (z === null) throw new Error("Kein Benutzer offen.");
      const e = entwurfVon(z);
      const aenderungen = funktionsAenderungen(z);
      // Die Funktionen ZUERST hier prüfen, mit derselben Regel wie der Server
      // (`funktionFehler`): Ein Befund blockiert das Speichern ganz — keine Rolle wird
      // geschrieben, der Fehler steht am Feld der Zeile (Review 5, W1).
      const vorab = aenderungen.flatMap((a) => {
        const befund = a.jobTitle === null ? null : funktionFehler(a.jobTitle);
        return befund === null ? [] : [{ field: funktionsFeld(a.companyId), message: befund }];
      });
      if (vorab.length > 0) {
        throw new ApiError(422, FUNKTION_UNGUELTIG, "Die Eingaben sind nicht gültig.", vorab);
      }
      let rolleGespeichert = false;
      try {
        let neu = z;
        if (rolleGeaendert(z)) {
          neu = await p.client.setUserRole(z.id, z.version, e.role, e.homeCompanyId);
          // Die ANTWORT übernehmen, nicht den eigenen Entwurf: Sie trägt die neue Version,
          // und ohne sie scheiterte das nächste Speichern derselben Person am
          // Versionsvergleich (der Server antwortete mit 409, obwohl niemand dazwischenkam).
          const stand = neu;
          setZeilen((alt) => (alt === null ? alt
            : alt.map((x) => (x.id === stand.id ? stand : x))));
          vergissRolle(z.id);
          rolleGespeichert = true;
        }
        if (aenderungen.length > 0) {
          // Zweiter Schritt: die Funktionen (ohne Version). Scheitert er TROTZ der
          // Vorabprüfung, ist die Rolle schon gespeichert — das sagt die Meldung
          // ausdrücklich (kein stiller Teilstand); das nächste Speichern schickt nur noch
          // die Funktionen.
          try {
            const antwort = await p.client.setUserJobTitles(z.id, aenderungen);
            setFunktionen((alt) => ({ ...alt, [z.id]: { art: "da", items: antwort.items } }));
          } catch (fehler) {
            if (!rolleGespeichert) throw fehler;
            if (fehler instanceof ApiError && fehler.status === 422
              && (fehler.fields ?? []).length > 0) {
              // Die Feldbefunde bleiben am Feld; der unbekannte Feldname trägt die Meldung
              // in die Sammelzeile des Formulars.
              throw new ApiError(422, fehler.code, TEILSTAND_MELDUNG,
                [...(fehler.fields ?? []), { field: "teilstand", message: TEILSTAND_MELDUNG }]);
            }
            const grund = fehler instanceof Error && fehler.message !== ""
              ? ` (${fehler.message})` : "";
            throw new Error(`${TEILSTAND_MELDUNG}${grund}`);
          }
        }
        vergiss(z.id);
        return neu;
      } catch (fehler) {
        if (fehler instanceof ApiError && fehler.status === 409) {
          fremderStand.current = alsBenutzer(fehler.conflict?.current);
        }
        // LETZTER_ADMIN ohne Feldangabe: der eigene Satz an der Rolle — sonst stünde gar
        // nichts dort, wo der Fehler entstanden ist.
        if (fehler instanceof ApiError && fehler.status === 422
          && fehler.code === "LETZTER_ADMIN" && (fehler.fields ?? []).length === 0) {
          throw new ApiError(422, fehler.code, fehler.message,
            [{ field: "role", message: LETZTER_ADMIN_ERSATZ }]);
        }
        throw fehler;
      }
    },
    neuLaden: async () => {
      let fremd = fremderStand.current;
      fremderStand.current = null;
      const id = satz?.id;
      if (id === undefined) return;
      if (fremd === null) {
        const antwort = await listUsers();
        setZeilen(antwort.items);
        fremd = antwort.items.find((x) => x.id === id) ?? null;
      }
      if (fremd !== null) {
        const stand = fremd;
        setZeilen((alt) => (alt === null ? alt
          : alt.map((x) => (x.id === stand.id ? stand : x))));
      }
      vergissRolle(id);
    },
    bekannteFelder: (feld) => feld === "role" || feld === "homeCompanyId"
      || feld.startsWith("funktionen."),
    beschriftungen: {
      role: "Rolle", homeCompanyId: "Heimatunternehmen",
      ...Object.fromEntries((benutzerId === null ? [] : geladeneFunktionen(benutzerId))
        .map((f) => [funktionsFeld(f.companyId), `Funktion bei ${f.companyName}`])),
    },
  });

  // ---------------------------------------------------------------------------
  // Die Objektliste — linke Spalte des Inhalts
  // ---------------------------------------------------------------------------
  const liste = (
    <>
      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {ladeFehler === null && zeilen === null && (
        <p className="vw__hinweis" role="status">Lädt …</p>
      )}
      {ladeFehler === null && zeilen !== null && zeilen.length === 0 && (
        <p className="vw__hinweis">Es ist noch niemand angemeldet gewesen.</p>
      )}

      <Objektliste label="Benutzer">
        {(zeilen ?? []).map((z) => (
          <Objektzeile
            key={z.id}
            name={z.name}
            /* Die E-Mail steht in Zeile 2 und nicht als `kennung`: Sie ist zu lang für die
               erste Zeile (dort stünde sie neben dem Namen und schnitte ihn ab) und sie ist
               keine kurze Nummer. */
            meta={z.email}
            {...(z.active ? {} : { marken: ["gesperrt"] })}
            gewaehlt={z.id === benutzerId}
            gesperrt={bearbeitung}
            /* Der zugängliche Name nennt Namen UND E-Mail: Zwei Kolleginnen können denselben
               Namen tragen, die Anmeldeadresse ist eindeutig. */
            ariaLabel={`Benutzer ${z.name}, ${z.email}, anzeigen`}
            onOeffnen={() => p.onOeffnen(z.id)}
          />
        ))}
      </Objektliste>
    </>
  );

  // ---------------------------------------------------------------------------
  // Das Detail — das Formular GENAU EINER Person
  // ---------------------------------------------------------------------------
  const detail = ((): ReactElement => {
    if (benutzerId === null) {
      return (
        <p className="vw__hinweis">
          Links einen Benutzer wählen — Rolle und Heimatunternehmen stehen dann hier. Das
          Heimatunternehmen bestimmt Logo und Farbakzent im Kopf.
        </p>
      );
    }
    if (satz === null) {
      /*
       * Die dreistufige Auskunft für ein Lesezeichen auf jemanden, den die Liste nicht
       * enthält (Etappe-1-Lektion): „steht nicht in der Liste" darf erst behauptet werden,
       * wenn es eine Liste GIBT.
       */
      if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
      if (zeilen === null) return <p className="vw__hinweis">Lädt …</p>;
      return (
        <p className="vw__hinweis">
          Diesen Benutzer gibt es nicht (mehr) — bitte links einen aus der Liste wählen.
        </p>
      );
    }

    const z = satz;
    const e = entwurfVon(z);
    const geaendert = istGeaendert(z);
    const feldFehler = formular.feldFehler;
    /* Das Panel gehört DER Person, über der es geöffnet wurde (Fixrunde-1-Muster): Ohne
       diesen Vergleich überlebte es einen Adresswechsel, den dieses Detail nicht auslöst —
       die Zurück-Taste etwa —, und sein Speichern-Knopf schriebe an Person A, während B
       darunter stünde. */
    const panelOffen = aendern === z.id;
    const funktionsStand = funktionen[z.id];
    const gesetzteFunktionen = geladeneFunktionen(z.id).filter((f) => f.jobTitle !== null);

    /** Abbrechen — mit Rückfrage, wenn ein Entwurf offen ist, und dann VERWIRFT es ihn.
     *  Anders sagte die Rückfrage die Unwahrheit: Die Entwürfe liegen je Kennung und
     *  stünden beim nächsten Öffnen wieder da (dieselbe Lehre wie beim Verlassen-Wächter
     *  oben, Fixrunde 1). */
    async function abbrechen(): Promise<void> {
      // Kein Ausgang, solange geschrieben wird — Escape wie Knopf (Fixrunde 1).
      if (formular.laeuft) return;
      if (!(await darfAbbrechen(geaendert))) return;
      vergiss(z.id);
      formular.zuruecksetzen();
      setAendern(null);
    }

    async function speichern(): Promise<void> {
      // Der EINE erfolgreiche Ausgang des strikten Panels. Bei 422 oder 409 bleibt es
      // stehen: Dort ist der Fehler entstanden, dort muss er zu lesen und zu beheben sein.
      if (await formular.aktion() !== undefined) setAendern(null);
    }

    return (
      /*
       * Eigene Identität je Person (Etappe-1-Lektion `key={objekt.id}`): Ohne diesen
       * Schlüssel nähme ein Adresswechsel den DOM-Zustand dieses Formulars auf die nächste
       * Person mit.
       */
      <div key={z.id} className="vw__eintrag">
        <div className="vw__eintrag-kopf">
          <span className="vw__eintrag-name">{z.name}</span>
          <Chip className="vw__marke">{z.active ? "aktiv" : "gesperrt"}</Chip>
        </div>
        <p className="vw__eintrag-zusatz">{z.email}</p>

        {/*
          * Das Detail LIEST (Hausregel „striktes Modalpanel", 06.09.2026): Rolle und
          * Heimatunternehmen stehen hier als Text, geändert wird im Panel. Bis dahin waren
          * es zwei Auswahlfelder mitten im Inhalt — man konnte sie anfassen, weggehen und
          * nie erfahren, dass etwas Ungespeichertes zurückblieb.
          *
          * Gezeigt wird der ENTWURF, wo einer liegt: Bricht jemand ab, ist er weg; steht er
          * noch (etwa nach einem abgelehnten Speichern), soll das Detail nicht behaupten,
          * es gelte schon wieder der Serverstand. Die Marke daneben sagt es ausdrücklich.
          */}
        <span className="vw__eintrag-wert">{ROLLEN_LABEL[e.role]}</span>
        <span className="vw__eintrag-zusatz">
          Heimatunternehmen: {heimatName(e.homeCompanyId)}
        </span>
        {/* Die Funktion je Unternehmen LESEND (Belegdesign Etappe 2) — nur die
            hinterlegten; der gespeicherte Stand, nicht der Entwurf (die Marke daneben
            sagt, dass noch etwas offen ist). */}
        {funktionsStand?.art === "fehler" && <Meldung text={funktionsStand.text} />}
        {funktionsStand?.art === "da" && (
          gesetzteFunktionen.length === 0
            ? <span className="vw__eintrag-zusatz">Funktion je Unternehmen: keine</span>
            : gesetzteFunktionen.map((f) => (
              <span key={f.companyId} className="vw__eintrag-zusatz">
                Funktion bei {f.companyShortCode} · {f.companyName}: {f.jobTitle}
              </span>
            ))
        )}
        {geaendert && <Chip className="vw__marke">ungespeichert</Chip>}

        <div className="vw__eintrag-aktionen">
          <KiKnopf name={`${z.name} ändern`} onClick={() => { formular.zuruecksetzen(); setAendern(z.id); }}>
            Ändern
          </KiKnopf>
        </div>

        {/* Die UNTERSCHRIFT (Layout-Editor Etappe 1) — sofort wirksam wie die Firmenlogos;
            sie gehört nicht ins Änderungspanel, weil dort ein Abbrechen sie nicht
            zurücknähme. Die Antwort ersetzt die Zeile (Version unverändert). */}
        <UnterschriftBlock client={p.client} benutzer={z}
          onGeaendert={(neu) => setZeilen((alt) => (alt === null ? alt
            : alt.map((x) => (x.id === neu.id ? neu : x))))} />

        {/* Das Änderungspanel steht bei seinem Knopf, also im Detail — und gehört genau
            dieser Person (siehe `panelOffen`). */}
        {panelOffen && (
          <Modal titel={`${z.name} ändern`} onAbbrechen={abbrechen}
            abbrechenGesperrt={formular.laeuft}>
            {formular.meldung !== null && <Meldung text={formular.meldung} />}

            <Formular beschriftung="oben" {...formular.formularProps}
              hinweis={formular.zustand === "fremder-stand" ? (
                <span>
                  {formular.fremderStand !== null
                    ? <>{formular.fremderStand.changedBy} hat diesen Benutzer inzwischen
                        geändert ({formatKonfliktZeit(formular.fremderStand.changedAt)}).</>
                    : <>Dieser Benutzer wurde inzwischen geändert.</>}
                  {" "}Deine Eingaben sind nicht verloren — „Neu laden" übernimmt den
                  aktuellen Stand.
                </span>
              ) : undefined}>
              <Auswahl id={`vw-rolle-${z.id}`} name={`vw-rolle-${z.id}`}
                beschriftung={`Rolle von ${z.name}`} wert={e.role}
                {...(feldFehler["role"] !== undefined ? { fehler: feldFehler["role"] } : {})}
                onGeaendert={(ev) => {
                  aendere(z, { role: ev.wert as Benutzer["role"] });
                  formular.feldFehlerRaeumen("role");
                }}>
                {ROLLEN.map((r) => (
                  <option key={r} value={r}>{ROLLEN_LABEL[r]}</option>
                ))}
              </Auswahl>

              <Auswahl id={`vw-heimat-${z.id}`} name={`vw-heimat-${z.id}`}
                beschriftung={`Heimatunternehmen von ${z.name}`}
                wert={e.homeCompanyId ?? ""}
                {...(feldFehler["homeCompanyId"] !== undefined
                  ? { fehler: feldFehler["homeCompanyId"] } : {})}
                onGeaendert={(ev) => {
                  aendere(z, { homeCompanyId: ev.wert === "" ? null : ev.wert });
                  formular.feldFehlerRaeumen("homeCompanyId");
                }}>
                <option value="">keines</option>
                {p.companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
                ))}
              </Auswahl>

              {/* Funktion je Unternehmen (Belegdesign Etappe 2, Entscheid 74): eine Zeile
                  je Unternehmen, leer heisst „keine". */}
              <fieldset className="vw__funktionen">
                <legend className="vw__label">Funktion je Unternehmen</legend>
                <p className="vw__hinweis">{FUNKTION_HINWEIS}</p>
                {funktionsStand?.art === "laedt" && (
                  <p className="vw__hinweis" role="status">Funktionen werden geladen …</p>
                )}
                {funktionsStand?.art === "fehler" && <Meldung text={funktionsStand.text} />}
                {geladeneFunktionen(z.id).map((f) => {
                  const feld = funktionsFeld(f.companyId);
                  return (
                    <Feld key={f.companyId} id={`vw-funktion-${z.id}-${f.companyId}`}
                      label={`Funktion bei ${f.companyShortCode} · ${f.companyName}`}
                      bearbeitbar max={FUNKTION_MAX}
                      wert={e.funktionen[f.companyId] ?? f.jobTitle ?? ""}
                      fehler={feldFehler[feld]}
                      onChange={(w) => {
                        aendereFunktion(z, f.companyId, w);
                        formular.feldFehlerRaeumen(feld);
                      }} />
                  );
                })}
              </fieldset>
            </Formular>

            <ModalFuss speichernText="Speichern"
              speichernGesperrt={!geaendert || formular.zustand !== "bereit"}
              /* Nur während des laufenden Schreibens — nicht an `geaendert`: Ein Panel
                 ohne Eingabe muss gerade DANN abbrechbar sein. */
              abbrechenGesperrt={formular.laeuft}
              speichernLabel={`Änderungen an ${z.name} speichern`}
              onSpeichern={() => { void speichern(); }}
              onAbbrechen={abbrechen} />
          </Modal>
        )}
      </div>
    );
  })();

  return { liste, detail: <>{detail}{bestaetigung}</> };
}
